import React, { useEffect, useState, useContext, useRef, memo } from "react";
import { Polygon, InfoWindow } from "@react-google-maps/api";
import axios from "axios";
import {
  getTopMarketShareData,
  getTopMSTooltipData,
} from "../../../../services/api/marketShare.api";
import { getPolygonByAdminLevel } from "../../../../services/api/mapLocation.api";
import {
  getPolygonFromApiResp,
  getPolygonsWithMarketShare,
  paginatedApiInvoker,
} from "../../../../utils/webMapApiHelper";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";
import { pageSizeByADM } from "../../../../constants/WebMapApi";
import { bestTopTypeKeyMapper, MapTools, Modules, TooltipType } from "../../../../constants/WebMap";
import TopMSTooltip from "../../TopMSTooltip";
import { getMiddle } from "../../../../helpers/webMap";
import { BackDropWithText } from "../../Loader";
import { TOP_MS_TRANSMISSION_DATA } from "../../../../constants/Data";

const TopMSMapElements = ({ filters, msType, eyeIcon, borderLvls, permittedOperators }) => {
  let cancelToken;
  let timeOutForApiCall = 2000;
  const polygonApiTO = useRef();

  const [msPolygons, setMsPolygons] = useState([]);
  const [msPolygonBorder, setMsPolygonBorder] = useState([]);
  const [topTooltip, setTopTooltip] = useState();
  const [isLoadingMarketShare, setIsLoadingMarketShare] = useState(false);
  const { appliedFilters, msConfig, setMarketShareLegendData, globalConfig, setApisOngoing, mapTool, setDownloadData } = useContext(WebMapContext);
  const { countryCode, year, quarter, level, levelVal } = appliedFilters;

  useEffect(() => {
    setTopTooltip(null)
  }, [filters, msType, eyeIcon, levelVal, level])

  useEffect(() => {
    setMsPolygons([]);
  }, [countryCode, year, quarter]);

  useEffect(() => {
    if (filters) drawOnMap();
  }, [JSON.stringify(filters), JSON.stringify(borderLvls), level, levelVal]);

  const drawOnMap = async () => {
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    setMsPolygons([])
    //For download feature
    setDownloadData(prevData => ({
      ...prevData,
      [Modules.MARKET_SHARE_TOP]: []
    }));
    setMsPolygonBorder([])
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(polygonApiTO.current);

    setIsLoadingMarketShare(true);
    polygonApiTO.current = setTimeout(async () => {
      try {
        const { data: msResp } = await getTopMarketShareData(
          {
            country: countryCode,
            quarter: `${quarter} ${year}`,
            borderLvl: filters.lvl,
            connectionType: filters.ct,
            ...(filters.aggType? {aggType: filters.aggType} : {}),
            ...(level && { level }),
            ...(levelVal && { levelVal }),
          },
          100000,
          1,
          cancelToken
        );
        const dataLvl = filters.lvl === "ADM1" ? "ADM2" : filters.lvl; // due to overlay issue in ADM1, polygons will fetch for ADM2 & 3 only
        const perPage = pageSizeByADM[`${countryCode}_${dataLvl}`] || 70;
        await paginatedApiInvoker(
          (pageSize, page) =>
            getPolygonByAdminLevel(
              {
                borderLvl: dataLvl,
                ...appliedFilters,
              },
              pageSize,
              page,
              true,
              cancelToken
            ),
          perPage,
          () => {
            setMsPolygons([]);
            //For download feature
            setDownloadData((prevData) => ({
              ...prevData,
              [Modules.MARKET_SHARE_TOP]: [],
            }));
            setApisOngoing((count) => count + 1);
          },
          (data) => {
            if (data) {
              const marketShares = getPolygonsWithMarketShare(
                data,
                msResp,
                filters.lvl
              );
              setMsPolygons(prevState => [...prevState, ...marketShares]);
              //For download feature
              setDownloadData(prevData => ({
                ...prevData,
                [Modules.MARKET_SHARE_TOP]: [
                  ...(prevData[Modules.MARKET_SHARE_TOP] || []),
                  ...marketShares
                ]
              }));
            }
          },
          () => setApisOngoing(count => count - 1),
          true
        );

        // only for ADM1, borders will set from another api due to overlay issues
        if (filters.lvl === "ADM1") {
          const perPageBorderApi = pageSizeByADM[`${countryCode}_${filters.lvl}`] || 70;
          await paginatedApiInvoker(
            (pageSize, page) =>
              getPolygonByAdminLevel(
                {
                  borderLvl: filters.lvl,
                  ...appliedFilters
                },
                pageSize,
                page,
                true,
                cancelToken
              ),
            perPageBorderApi,
            () => setMsPolygonBorder([]),
            (data) => {
              if (data) {
                const polygons = getPolygonFromApiResp(data);
                setMsPolygonBorder(prevState => [...prevState, ...polygons]);
              }
            },
            null,
            false
          );
        }
      } catch (e) {
        console.log(e)
      } finally {
        setIsLoadingMarketShare(false);
      }
    }, timeOutForApiCall);
  };

  const filterOperatorDetails = (msData) => {
    return permittedOperators.find((op) => op.operator === msData?.ms?.[msType]);
  };

  const onClickGeohash = async (operatorData) => {
    try {
      const polygon = operatorData.poly;
      let response;
      let lvls = []
      if (operatorData && operatorData.ms) {
        const adm1 = operatorData.ms.adm1;
        const adm2 = operatorData.ms.adm2 || null;
        const adm3 = operatorData.ms.adm3 || null;

        lvls = [{ [globalConfig?.adminLevelLabels?.adm1]: adm1 }]
        if (adm2) lvls.push({ [globalConfig?.adminLevelLabels?.adm2]: adm2 })
        if (adm3) lvls.push({ [globalConfig?.adminLevelLabels?.adm3]: adm3 })

        let aggrType = filters.aggType;
        const dataTransmitMetrics = [bestTopTypeKeyMapper.data_received, bestTopTypeKeyMapper.data_transceived, bestTopTypeKeyMapper.data_transmitted];
        if (dataTransmitMetrics.includes(msType)) aggrType = "Mean";

        response = await getTopMSTooltipData({
          country: appliedFilters.countryCode,
          quarter: `${quarter} ${year}`,
          ct: filters.ct,
          adm1: adm1,
          adm2: adm2,
          adm3: adm3,
          borderLvl: filters.lvl || "ADM1",
          at: aggrType,
        });
      }
      if (response && response.success) {
        const updatedData = response.data.op.map(item => {
          const permittedOperator = permittedOperators.find(op => op.operator === item.op);

          if (permittedOperator) {
            if (!permittedOperator.isAnonymized) {
              return {
                ...item,
                name: permittedOperator.name,
                isAnonymized: false,
              };
            } else {
              const anonymized = response.anomData.find(anon => anon.operator === item.op);
              return {
                ...item,
                name: anonymized ? anonymized.anonymizedName : "",
                isAnonymized: true,
              };
            }
          } else {
            const anonymized = response.anomData.find(anon => anon.operator === item.op);
            return {
              ...item,
              name: anonymized ? anonymized.anonymizedName : "",
              isAnonymized: true,
            };
          }
        });
        const data = {
          ...response.data,
          op: updatedData,
        };
        setTopTooltip({
          ...operatorData,
          polygon,
          data: data,
          lvls: lvls
        });
      }
    } catch (err) {
      console.log("Erro while getting tooltip data : ", err);
    }
  };


  useEffect(() => {
    if (eyeIcon && msConfig && permittedOperators) generateMarketShareLegend();
    else setMarketShareLegendData([]);
  }, [eyeIcon, msConfig, permittedOperators]);

  const generateMarketShareLegend = (

  ) => {
    const notPermitted ={
      isNotPermitted: true,
      text: 'Not Permitted'
    }
    setMarketShareLegendData([
      {
        title: "Operators",
        values: permittedOperators?.filter(op => op.type === "MNO" && op.isAnonymized === false).map((op) => ({
          markerType: "colorBox",
          markerColor: op.color,
          img: op.icon,
        })),
        notPermitted
      },
    ]);
  };

  const findCenter = (polygons) => {
    return {
      lat: getMiddle('lat', polygons),
      lng: getMiddle('lng', polygons)
    }
  }

  return (
    <>
      {isLoadingMarketShare && (
        <BackDropWithText text={"Loading Market Share Data..."} />
      )}
      {msPolygons.length > 0
        ? <div className="data-on-map">
          {msPolygons?.map((data, index) => {
            const bestOprt = filterOperatorDetails(data);
            return bestOprt ? (
              <Polygon
                path={data.poly}
                key={index}
                options={{
                  fillColor: bestOprt.isAnonymized ? "#B4B4B4" : bestOprt?.color,
                  fillOpacity: 0.6,
                  strokeColor: filters.lvl !== "ADM1" ? "grey" : bestOprt?.color,
                  strokeOpacity: filters.lvl !== "ADM1" ? 0.8 : 0.2,
                  strokeWeight: filters.lvl !== "ADM1" ? 0.5 : 0.2,
                  clickable: mapTool === MapTools.DOWNLOAD_PIN || mapTool === MapTools.DOWNLOAD_POLYGON ? false: true ,
                  draggable: false,
                  editable: false,
                  geodesic: false,
                  zIndex: 1,
                }}
                onClick={() => {
                  onClickGeohash(data);
                }}
              />
            ) : (
              <></>
            );
          })}
        </div>
        : <></>}
      {filters.lvl === "ADM1"
        ? msPolygonBorder?.map((data, index) => {
          return <Polygon
            path={data}
            key={index}
            options={{
              fillColor: "white",
              fillOpacity: 0,
              strokeColor: "grey",
              strokeOpacity: 0.8,
              strokeWeight: 0.8,
              clickable: false,
              draggable: false,
              editable: false,
              geodesic: false,
              zIndex: 1,
            }}
          />
        })
        : <></>}
      {topTooltip && (
        <InfoWindow
          position={findCenter(topTooltip?.polygon)}
          onCloseClick={() => {
            setTopTooltip(null);
          }}
          options={{ disableAutoPan: true }}
        >
          <TopMSTooltip
            supplyType={msType}
            geohashStat={topTooltip}
            type={TooltipType.BEST}
            topMarketShareToggle={true}
            permittedOperators={permittedOperators}
          />
        </InfoWindow>
      )}
    </>
  );
};

export default memo(TopMSMapElements);
